import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, X, Eye } from 'lucide-react';

const ReviewPhotosDisplay = ({ photos = [] }) => {
    const [lightboxOpen, setLightboxOpen] = useState(false);
    const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);

    if (!photos || photos.length === 0) {
        return null;
    }

    const openLightbox = (index) => {
        setCurrentPhotoIndex(index);
        setLightboxOpen(true);
    };

    const closeLightbox = () => {
        setLightboxOpen(false);
    };

    const nextPhoto = () => {
        setCurrentPhotoIndex((prev) => (prev + 1) % photos.length);
    };

    const prevPhoto = () => {
        setCurrentPhotoIndex((prev) => (prev - 1 + photos.length) % photos.length);
    };

    const getTypeLabel = (type) => {
        const labels = {
            'campsite': 'Campsite',
            'view': 'View',
            'amenity': 'Amenity',
            'road_access': 'Access',
            'other': 'Other'
        };
        return labels[type] || 'Photo';
    };

    return (
        <>
            {/* Photo Grid */}
            <div className="mt-3">
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                    {photos.map((photo, index) => (
                        <div
                            key={photo.id || index}
                            className="relative group cursor-pointer"
                            onClick={() => openLightbox(index)}
                        >
                            <div className="relative w-full" style={{ paddingBottom: '100%' }}>
                                <img
                                    src={photo.thumbnail_url || photo.photo_url}
                                    alt={photo.caption || `Review photo ${index + 1}`}
                                    className="absolute inset-0 w-full h-full object-cover rounded-md border border-gray-200 hover:scale-105 transition-transform duration-200"
                                />
                                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all rounded-md flex items-center justify-center pointer-events-none">
                                    <Eye className="h-4 w-4 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                                </div>
                                <div className="absolute bottom-1 left-1 bg-black bg-opacity-60 text-white text-xs px-1 py-0.5 rounded pointer-events-none">
                                    {getTypeLabel(photo.photo_type)}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Lightbox Modal */}
            {lightboxOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-4">
                    <div className="relative max-w-4xl max-h-full">
                        {/* Close button */}
                        <button
                            onClick={closeLightbox}
                            className="absolute top-4 right-4 z-10 p-2 bg-black bg-opacity-50 text-white rounded-full hover:bg-opacity-70"
                        >
                            <X className="h-5 w-5" />
                        </button>

                        {/* Navigation arrows */}
                        {photos.length > 1 && (
                            <>
                                <button
                                    onClick={prevPhoto}
                                    className="absolute left-4 top-1/2 transform -translate-y-1/2 z-10 p-2 bg-black bg-opacity-50 text-white rounded-full hover:bg-opacity-70"
                                >
                                    <ChevronLeft className="h-6 w-6" />
                                </button>
                                <button
                                    onClick={nextPhoto}
                                    className="absolute right-4 top-1/2 transform -translate-y-1/2 z-10 p-2 bg-black bg-opacity-50 text-white rounded-full hover:bg-opacity-70"
                                >
                                    <ChevronRight className="h-6 w-6" />
                                </button>
                            </>
                        )}

                        {/* Photo */}
                        <img
                            src={photos[currentPhotoIndex].photo_url}
                            alt={photos[currentPhotoIndex].caption || `Review photo ${currentPhotoIndex + 1}`}
                            className="max-w-full max-h-full object-contain"
                        />

                        {/* Photo info */}
                        <div className="absolute bottom-4 left-4 right-4 bg-black bg-opacity-60 text-white p-4 rounded-lg">
                            <div className="flex justify-between items-start">
                                <div>
                                    <div className="text-sm font-medium">
                                        {getTypeLabel(photos[currentPhotoIndex].photo_type)}
                                        {photos.length > 1 && ` (${currentPhotoIndex + 1} of ${photos.length})`}
                                    </div>
                                    {photos[currentPhotoIndex].caption && (
                                        <div className="text-sm mt-1 opacity-90">
                                            {photos[currentPhotoIndex].caption}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default ReviewPhotosDisplay;